// OralExamVoicePage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useOralVoiceStartup, MICROPHONE_TIMEOUT_MS, CONNECTION_TIMEOUT_MS } from "./oralVoiceStartup";
import OralVoiceRecovery from "./OralVoiceRecovery";
import PublicAccountSummary from "./PublicAccountSummary";
import { PUBLIC_SESSION_CONFIG, getPublicSession, getPublicSessionPayload, isPublicSessionError, logoutPublicSession } from "./publicAccountSession";
import {
  OpenAIRealtimeWebRTC,
  RealtimeAgent,
  RealtimeSession,
  tool,
} from "@openai/agents/realtime";
import { z } from "zod";
import "./OralExamVoicePage.css";
import {
  buildInterviewStartPayload,
  getInterviewPacks,
  getPracticeSelectorItems,
} from "./interviewPracticeConfig";
import {
  getFirstSelectableTopicId,
  getOralAccessNotice,
  getTopicId,
  getTopicLockMessage,
  isTopicSelectable,
} from "./oralTopicAccess";
import OralTopicSelect from "./OralTopicSelect";

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const labelStyle = {
  fontWeight: 700,
  marginBottom: 6,
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 14,
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 700,
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "#e5e7eb",
  color: "#111827",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#3F51B5",
  color: "#ffffff",
};

const dangerButtonStyle = {
  ...buttonStyle,
  background: "#b42318",
  color: "#ffffff",
};

const ORAL_DEBUG =
  window.location.hostname === "sandbox-schools.articulate.com.sg";
const PUBLIC_ORAL_PRODUCTS_URL = "https://www.articulate.com.sg/products";
const POST_ASSISTANT_AUDIO_MIC_BUFFER_MS = 250;
const COMPLETION_CLEANUP_FALLBACK_MS = 18000;
const PAUSE_DURATION_OPTIONS = [
  { value: "fast", label: "Short" },
  { value: "normal", label: "Normal (Recommended)" },
  { value: "patient", label: "Extended" },
];

const LISTENING_STATE_CONFIG = {
  teacher_speaking: {
    label: "Teacher is speaking — please wait",
    border: "#eab308",
    background: "#fef9c3",
    text: "#713f12",
  },
  muted: {
    label: "Mic opening — wait for green",
    border: "#f97316",
    background: "#ffedd5",
    text: "#9a3412",
  },
  ready: {
    label: "Ready — answer now",
    border: "#22c55e",
    background: "#dcfce7",
    text: "#166534",
  },
  thinking: {
    label: "Thinking time",
    border: "#6366f1",
    background: "#eef2ff",
    text: "#3730a3",
  },
  processing: {
    label: "Processing answer",
    border: "#9ca3af",
    background: "#f3f4f6",
    text: "#374151",
  },
  paused: {
    label: "Paused — take your time",
    border: "#6366f1",
    background: "#eef2ff",
    text: "#3730a3",
  },
};

const DEFAULT_LANGUAGES = [
  { language_id: "english", language_name: "English" },
];

function getSafeLanguages(payload) {
  return Array.isArray(payload?.languages) && payload.languages.length > 0
    ? payload.languages
    : DEFAULT_LANGUAGES;
}

function getSafeLanguageId(language) {
  return language?.language_id || "english";
}

function getDefaultLanguageId(payload, fallbackLanguageId = "english") {
  const safeLanguages = getSafeLanguages(payload);
  const firstLanguageId = getSafeLanguageId(safeLanguages[0]);
  const payloadLanguageId = payload?.language_id || "";
  const payloadLanguageIsSupported = safeLanguages.some(
    (language) => getSafeLanguageId(language) === payloadLanguageId
  );

  return (
    (payloadLanguageIsSupported && payloadLanguageId) ||
    firstLanguageId ||
    fallbackLanguageId ||
    "english"
  );
}

function mapBackendTurnDetectionToSdk(turnDetection = {}) {
  return {
    type: turnDetection.type || "server_vad",
    threshold: Number(turnDetection.threshold ?? 0.7),
    prefixPaddingMs: Number(
      turnDetection.prefix_padding_ms ?? turnDetection.prefixPaddingMs ?? 1500
    ),
    silenceDurationMs: Number(
      turnDetection.silence_duration_ms ??
        turnDetection.silenceDurationMs ??
        1300
    ),
    createResponse: Boolean(
      turnDetection.create_response ?? turnDetection.createResponse ?? false
    ),
    interruptResponse: Boolean(
      turnDetection.interrupt_response ??
        turnDetection.interruptResponse ??
        false
    ),
  };
}

function getTopicStimulusMedia(topic, sessionInfo) {
  const media = topic?.stimulus_media || sessionInfo?.stimulusMedia || null;

  if (media?.media_type) {
    return {
      mediaType: media.media_type || "none",
      sourceType: media.source_type || "",
      displayUrl: media.display_url || "",
      videoUrl: media.video_url || "",
      description:
        media.description ||
        topic?.picture_stimulus ||
        sessionInfo?.pictureStimulus ||
        "",
    };
  }

  if (topic?.picture_stimulus_url || sessionInfo?.pictureStimulusUrl) {
    return {
      mediaType: "image",
      sourceType: "legacy_picture_url",
      displayUrl:
        topic?.picture_stimulus_url || sessionInfo?.pictureStimulusUrl || "",
      videoUrl: "",
      description:
        topic?.picture_stimulus ||
        sessionInfo?.pictureStimulus ||
        "",
    };
  }

  return {
    mediaType: "none",
    sourceType: "",
    displayUrl: "",
    videoUrl: "",
    description:
      topic?.picture_stimulus ||
      sessionInfo?.pictureStimulus ||
      "",
  };
}

function getOrderedQuestions(questions) {
  return Object.entries(questions || {})
    .map(([key, text]) => {
      const match = /^question_(\d+)$/.exec(key);
      if (!match) {
        return null;
      }

      const trimmedText = String(text || "").trim();
      if (!trimmedText) {
        return null;
      }

      return {
        key,
        index: Number(match[1]),
        text: trimmedText,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
}

function getResponsePayload(rawData = {}) {
  if (
    rawData?.data &&
    typeof rawData.data === "object" &&
    !Array.isArray(rawData.data)
  ) {
    return {
      ...rawData,
      ...rawData.data,
    };
  }

  return rawData || {};
}

function getErrorResponseData(err) {
  return getResponsePayload(err?.response?.data || {});
}

function isInsufficientOralAccessPayload(status, data = {}) {
  const reason = data?.reason || "";
  const error = data?.error || "";
  const errorCode = data?.error_code || data?.code || "";

  return (
    status === 402 ||
    error === "insufficient_credits" ||
    errorCode === "INSUFFICIENT_CREDITS" ||
    reason === "insufficient_credit_or_subscription" ||
    reason === "paid_credit_or_subscription_required" ||
    reason === "insufficient_credit"
  );
}

function isInsufficientOralAccessError(err) {
  const status = err?.response?.status;
  const data = getErrorResponseData(err);
  return isInsufficientOralAccessPayload(status, data);
}

function PublicOralExamEmbedPage({ practiceType = "oral_exam" }) {
  const isInterviewPractice = practiceType === "interview_practice";
  const productNoun = isInterviewPractice ? "interview practice" : "oral practice";
  const [topicsData, setTopicsData] = useState(null);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topicsError, setTopicsError] = useState("");

  const [publicEmail, setPublicEmail] = useState("");
  const [checkingPublicSession, setCheckingPublicSession] = useState(true);
  const [publicSessionError, setPublicSessionError] = useState("");
  const [publicUser, setPublicUser] = useState(null);
  const [publicBalances, setPublicBalances] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [publicEmailCode, setPublicEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState("");
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [emailSendCooldown, setEmailSendCooldown] = useState(0);
  const [userId, setUserId] = useState("");
  const [studentName, setStudentName] = useState("");
  const [, setStudentRegNumber] = useState("");
  const [resolvedClassContext, setResolvedClassContext] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("");
  const [bootstrapErrorDetails, setBootstrapErrorDetails] = useState(null);
  const [selectedLanguageId, setSelectedLanguageId] = useState("");
  const [selectedStandard, setSelectedStandard] = useState("");
  const [selectedTopicSetId, setSelectedTopicSetId] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [selectedPauseDurationMode, setSelectedPauseDurationMode] =
    useState("normal");

  const voiceStartup = useOralVoiceStartup();
  const startRequestPendingRef = useRef(false);
  const [startingSession, setStartingSession] = useState(false);
  const voiceStartupLocked = startingSession || voiceStartup.busy || !!voiceStartup.error;
  const [sessionStatus, setSessionStatus] = useState("Not started");
  const [listeningState, setListeningState] = useState("muted");
  const [studentCanRespond, setStudentCanRespond] = useState(false);
  const [waitingForReadyPress, setWaitingForReadyPress] = useState(false);
  const [readyButtonPressedAt, setReadyButtonPressedAt] = useState("");
  const [ignoredTranscriptBeforeReady, setIgnoredTranscriptBeforeReady] =
    useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [realtimeCleanupInProgress, setRealtimeCleanupInProgress] =
    useState(false);
  const [reportUrl, setReportUrl] = useState("");
  const [reportStatus, setReportStatus] = useState("");

  const [sessionInfo, setSessionInfo] = useState({
    sessionId: "",
    status: "",
    languageId: "",
    standard: "",
    topicSetId: "",
    topicId: "",
    topicTitle: "",
    pictureStimulus: "",
    pictureStimulusUrl: "",
    stimulusMedia: {
      media_type: "",
      source_type: "",
      display_url: "",
      video_url: "",
      blob_name: "",
      description: "",
    },
    questions: {},
    questionCount: 0,
    currentQuestionIndex: 1,
    spokenPrompt: "",
  });

  const [eventLog, setEventLog] = useState([]);

  const sessionRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const startupGuardRef = useRef(false);
  const firstTutorUtteranceDoneRef = useRef(false);
  const startupUnlockTimerRef = useRef(null);
  // Deprecated: do not use old duration-based mute timing to control mic mute.
  // Mic gating is driven by realtime audio_start/audio_stopped output events.
  const promptMuteTimerRef = useRef(null);
  const completionCloseTimerRef = useRef(null);
  const completionCleanupFallbackTimerRef = useRef(null);
  const activeSessionKeyRef = useRef("");
  const completionCleanupSessionKeyRef = useRef("");
  const completionCleanupStartedRef = useRef(false);
  const micMutedRef = useRef(false);
  const realtimeResponseActiveRef = useRef(false);
  const realtimeResponseRequestPendingRef = useRef(false);
  const realtimeResponseRequestSourceRef = useRef("");
  const activeRealtimeResponseIdRef = useRef("");
  const assistantAudioActiveRef = useRef(false);
  const responseActiveDiagnosticTimerRef = useRef(null);
  const isPracticePausedRef = useRef(false);
  const prePauseListeningStateRef = useRef("ready");
  const listeningStateRef = useRef("muted");
  const sessionStatusRef = useRef("Not started");
  const studentCanRespondRef = useRef(false);
  const studentReadyToAnswerRef = useRef(false);
  const waitingForReadyPressRef = useRef(false);
  const pendingTutorPromptActionRef = useRef("next_question");
  const readyButtonPressedAtRef = useRef("");
  const ignoredTranscriptBeforeReadyRef = useRef(0);
  const frontendTurnIdRef = useRef(0);
  const lastAudioGateReleasedTurnIdRef = useRef(null);
  const emailVerificationUpdateRef = useRef(false);
  const bootstrapRequestKeyRef = useRef("");
  const sessionMetaRef = useRef({
    sessionId: "",
    currentQuestionIndex: 1,
    userEmail: "",
    userId: "",
    lastSpokenPrompt: "",
    closed: false,
  });

  const debugLog = (label, payload = {}) => {
    if (!ORAL_DEBUG) {
      return;
    }

    console.log(label, {
      timestamp: new Date().toISOString(),
      currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
      micMuted: micMutedRef.current,
      ...payload,
    });
  };

  const getOralDebugSnapshot = (overrides = {}) => ({
    timestamp: new Date().toISOString(),
    session_id: sessionMetaRef.current.sessionId || "",
    currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
    status: listeningStateRef.current || sessionStatusRef.current || "",
    micMuted: micMutedRef.current,
    answerCaptureReady: Boolean(studentCanRespondRef.current),
    studentReadyToAnswer: Boolean(studentReadyToAnswerRef.current),
    studentCanRespond: Boolean(studentCanRespondRef.current),
    waitingForReadyPress: Boolean(waitingForReadyPressRef.current),
    realtimeResponseActive: Boolean(realtimeResponseActiveRef.current),
    studentReadyToAnswerRefCurrent: Boolean(studentReadyToAnswerRef.current),
    studentCanRespondRefCurrent: Boolean(studentCanRespondRef.current),
    realtimeResponseActiveRefCurrent: Boolean(
      realtimeResponseActiveRef.current
    ),
    transcriptLength:
      overrides.transcriptLength !== undefined ? overrides.transcriptLength : 0,
    transcriptPreview:
      overrides.transcriptPreview !== undefined
        ? String(overrides.transcriptPreview || "").slice(0, 200)
        : "",
    readyButtonPressedAt:
      overrides.readyButtonPressedAt !== undefined
        ? overrides.readyButtonPressedAt
        : readyButtonPressedAtRef.current || "",
    ignoredTranscriptBeforeReady:
      overrides.ignoredTranscriptBeforeReady !== undefined
        ? overrides.ignoredTranscriptBeforeReady
        : ignoredTranscriptBeforeReadyRef.current || 0,
    ...overrides,
  });

  const markAnswerCaptureReady = (reason) => {
    debugLog("[ORAL DEBUG] Answer capture ready", getOralDebugSnapshot({ reason }));
  };

  const clearTranscriptDebugBuffer = (reason) => {
    debugLog("[ORAL DEBUG] Transcript buffer clear/reset", getOralDebugSnapshot({ reason }));
  };

  const markPromptStarted = (reason, promptText = "") => {
    frontendTurnIdRef.current += 1;
    lastAudioGateReleasedTurnIdRef.current = null;
    debugLog(
      "[ORAL DEBUG] Realtime AI response start",
      getOralDebugSnapshot({
        reason,
        frontendTurnId: frontendTurnIdRef.current,
        transcriptPreview: String(promptText || "").slice(0, 200),
      })
    );
  };

  const setMicrophoneTrackEnabled = (enabled) => {
    const stream = microphoneStreamRef.current;
    if (!stream) {
      micMutedRef.current = !enabled;
      return 0;
    }

    const audioTracks = stream.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = enabled;
    });
    micMutedRef.current = !enabled;
    return audioTracks.length;
  };

  const setWaitingForReadyPressValue = (value) => {
    waitingForReadyPressRef.current = value;
    setWaitingForReadyPress(value);
  };

  const disableMicrophoneTrack = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(false);
    debugLog(
      "[ORAL DEBUG] Assistant audio started: mic disabled",
      getOralDebugSnapshot({ reason, trackCount })
    );
  };

  const enableMicrophoneAfterAudioBuffer = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(true);
    debugLog(
      "[ORAL DEBUG] Mic enabled after 250ms buffer",
      getOralDebugSnapshot({
        reason,
        delayMs: POST_ASSISTANT_AUDIO_MIC_BUFFER_MS,
        trackCount,
      })
    );
  };

  const enableMicrophoneForReadyPress = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(true);
    debugLog(
      "[ORAL DEBUG] Ready pressed: mic enabled",
      getOralDebugSnapshot({ reason, trackCount })
    );
  };

  const setThinkingAfterTutorPrompt = (reason) => {
    setMicrophoneTrackEnabled(false);
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(true);
    listeningStateRef.current = "thinking";
    sessionStatusRef.current =
      "Take your time. Tap Ready to Answer when prepared.";
    setStudentCanRespond(false);
    setListeningState("thinking");
    setSessionStatus("Take your time. Tap Ready to Answer when prepared.");
    debugLog("[ORAL DEBUG] Student thinking time started", getOralDebugSnapshot({ reason }));
  };

  const setReadyAfterTutorPrompt = (reason) => {
    enableMicrophoneAfterAudioBuffer(reason);
    studentReadyToAnswerRef.current = true;
    studentCanRespondRef.current = true;
    setWaitingForReadyPressValue(false);
    listeningStateRef.current = "ready";
    sessionStatusRef.current = "Listening \u2014 please answer now";
    setStudentCanRespond(true);
    setListeningState("ready");
    setSessionStatus("Listening \u2014 please answer now");
    markAnswerCaptureReady(reason);
  };

  const completeTutorPromptResponse = (reason) => {
    const promptAction = pendingTutorPromptActionRef.current || "next_question";

    if (sessionMetaRef.current.closed || promptAction === "completed") {
      studentReadyToAnswerRef.current = false;
      studentCanRespondRef.current = false;
      setWaitingForReadyPressValue(false);
      setStudentCanRespond(false);
      return;
    }

    if (promptAction === "followup") {
      setReadyAfterTutorPrompt(`${reason}: followup`);
      return;
    }

    setThinkingAfterTutorPrompt(`${reason}: ${promptAction}`);
  };

  const handleReadyToAnswer = () => {
    if (!isConnected || sessionMetaRef.current.closed) {
      return;
    }

    const pressedAt = new Date().toISOString();
    clearTranscriptDebugBuffer("student pressed Ready to Answer");
    enableMicrophoneForReadyPress("student pressed Ready to Answer");
    studentReadyToAnswerRef.current = true;
    studentCanRespondRef.current = true;
    setWaitingForReadyPressValue(false);
    readyButtonPressedAtRef.current = pressedAt;
    listeningStateRef.current = "ready";
    sessionStatusRef.current = "Listening \u2014 please answer now";
    setReadyButtonPressedAt(pressedAt);
    setStudentCanRespond(true);
    setListeningState("ready");
    setSessionStatus("Listening \u2014 please answer now");
    markAnswerCaptureReady("student pressed Ready to Answer");
  };

  const normalizeRealtimeError = (err = {}) => ({
    code:
      err?.error?.error?.code ||
      err?.error?.code ||
      err?.code ||
      err?.payload?.error?.error?.code ||
      err?.payload?.error?.code ||
      "",
    message:
      err?.error?.error?.message ||
      err?.error?.message ||
      err?.message ||
      err?.payload?.error?.error?.message ||
      err?.payload?.error?.message ||
      "",
  });

  const clearResponseActiveDiagnosticTimer = () => {
    if (responseActiveDiagnosticTimerRef.current) {
      clearTimeout(responseActiveDiagnosticTimerRef.current);
      responseActiveDiagnosticTimerRef.current = null;
    }
  };

  const startResponseActiveDiagnosticTimer = (source, sessionKey) => {
    clearResponseActiveDiagnosticTimer();
    responseActiveDiagnosticTimerRef.current = setTimeout(() => {
      if (
        sessionKey === activeSessionKeyRef.current &&
        (realtimeResponseRequestPendingRef.current ||
          realtimeResponseActiveRef.current ||
          activeRealtimeResponseIdRef.current ||
          assistantAudioActiveRef.current)
      ) {
        debugLog("[ORAL DEBUG] Realtime response still active", {
          source,
          sessionKey,
          responseRequestPending: realtimeResponseRequestPendingRef.current,
          activeResponseId: activeRealtimeResponseIdRef.current,
          assistantAudioActive: assistantAudioActiveRef.current,
          pendingPromptAction: pendingTutorPromptActionRef.current,
        });
      }
    }, 20000);
  };

  const maybeClearRealtimeResponseActive = (reason) => {
    if (
      !realtimeResponseRequestPendingRef.current &&
      !activeRealtimeResponseIdRef.current &&
      !assistantAudioActiveRef.current
    ) {
      realtimeResponseActiveRef.current = false;
      clearResponseActiveDiagnosticTimer();
      debugLog("[ORAL DEBUG] Realtime response active cleared", {
        ...getOralDebugSnapshot({ reason }),
        activeResponseId: activeRealtimeResponseIdRef.current,
        assistantAudioActive: assistantAudioActiveRef.current,
      });
    }
  };

  const sendTutorRealtimeMessage = (
    realtimeSession,
    promptText,
    source = "startup_force_prompt",
    sessionKey = activeSessionKeyRef.current
  ) => {
    const debugPayload = {
      source,
      sessionKey,
      frontendTurnId: frontendTurnIdRef.current,
      currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
      responseRequestPending: realtimeResponseRequestPendingRef.current,
      realtimeResponseActive: realtimeResponseActiveRef.current,
      activeResponseId: activeRealtimeResponseIdRef.current,
      assistantAudioActive: assistantAudioActiveRef.current,
      pendingPromptAction: pendingTutorPromptActionRef.current,
    };
    const blockedReason =
      sessionKey !== activeSessionKeyRef.current
        ? "stale session key"
        : sessionMetaRef.current.closed
        ? "session completed"
        : realtimeResponseRequestPendingRef.current
        ? "response request pending"
        : realtimeResponseActiveRef.current
        ? "response active"
        : activeRealtimeResponseIdRef.current
        ? "active response id present"
        : assistantAudioActiveRef.current
        ? "assistant audio active"
        : "";

    if (blockedReason) {
      debugLog("[ORAL DEBUG] Blocked realtime response request", {
        ...debugPayload,
        blockedReason,
        promptText,
      });
      return;
    }

    markPromptStarted("sendTutorRealtimeMessage", promptText);
    realtimeResponseRequestPendingRef.current = true;
    realtimeResponseRequestSourceRef.current = source;
    realtimeResponseActiveRef.current = true;
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(false);
    listeningStateRef.current = "teacher_speaking";
    setListeningState("teacher_speaking");
    debugLog("[ORAL DEBUG] Realtime response active set true", {
      ...debugPayload,
      promptText,
    });
    startResponseActiveDiagnosticTimer(source, sessionKey);

    realtimeSession.sendMessage(promptText);
  };

  const appendLog = (speaker, text) => {
    if (!text) return;
    setEventLog((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        speaker,
        text,
        at: new Date().toLocaleTimeString(),
      },
    ]);
  };

  const showOralAccessNeeded = (details = {}) => {
    setStudentCanRespond(false);
    setListeningState("muted");
    setBootstrapErrorDetails({
      credit_balance: details?.credit_balance,
      entitlement_source: details?.entitlement_source,
      reason:
        details?.reason ||
        details?.error ||
        details?.error_code ||
        "insufficient_credit_or_subscription",
      message: details?.lock_message || details?.message || "",
      payment_options: details?.payment_options || null,
    });
    setVerificationStatus(
      "You do not have enough oral credits or an active subscription. Please buy or renew access to continue."
    );
    setEmailVerificationStatus("Email verified, but oral access is not available.");
    setSessionStatus("Oral practice access needed.");
  };

  const handleInsufficientOralAccessError = (err, details = {}) => {
    const data = getErrorResponseData(err);
    showOralAccessNeeded({
      ...data,
      ...details,
      payment_options: data?.payment_options || details?.payment_options,
    });
  };

  const pausePractice = () => {
    if (!isConnected || isPracticePausedRef.current) {
      return;
    }

    prePauseListeningStateRef.current = listeningState;
    isPracticePausedRef.current = true;
    studentReadyToAnswerRef.current = false;
    setWaitingForReadyPressValue(false);
    setMicrophoneTrackEnabled(false);
    setStudentCanRespond(false);
    setListeningState("paused");
    setSessionStatus("Paused");
    appendLog("System", "Practice paused. Evaluation is temporarily disabled.");
  };

  const resumePractice = () => {
    if (!isConnected || !isPracticePausedRef.current) {
      return;
    }

    const previousListeningState = prePauseListeningStateRef.current;
    isPracticePausedRef.current = false;
    setMicrophoneTrackEnabled(previousListeningState === "ready");

    if (
      previousListeningState === "teacher_speaking" ||
      previousListeningState === "processing" ||
      previousListeningState === "muted"
    ) {
      studentCanRespondRef.current = false;
      setWaitingForReadyPressValue(false);
      setStudentCanRespond(false);
      setListeningState(previousListeningState);
      setSessionStatus(
        previousListeningState === "teacher_speaking"
          ? "Tutor speaking..."
          : previousListeningState === "processing"
          ? "Processing answer"
          : "Mic opening — wait for green"
      );
    } else {
      setThinkingAfterTutorPrompt("resumePractice");
    }

    appendLog("System", "Practice resumed.");
  };

  // eslint-disable-next-line no-unused-vars
  const togglePracticePause = () => {
    if (isPracticePausedRef.current) {
      resumePractice();
    } else {
      pausePractice();
    }
  };

  const waitForAssistantAudioEnd = (nextStatus = "awaiting_answer") => {
    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    const isStartupPrompt = !firstTutorUtteranceDoneRef.current;
    startupGuardRef.current = true;

    if (isPracticePausedRef.current) {
      setMicrophoneTrackEnabled(false);
      return;
    }
    setMicrophoneTrackEnabled(false);
    debugLog("[ORAL DEBUG] Mic disabled; waiting for assistant audio end", {
      nextStatus,
      isStartupPrompt,
    });
    if (nextStatus === "completed") {
      setSessionStatus("Completed");
      return;
    }
  };

  const releaseAssistantAudioGateAfterBuffer = (reason) => {
    const currentTurnId = frontendTurnIdRef.current;

    if (lastAudioGateReleasedTurnIdRef.current === currentTurnId) {
      debugLog(
        "[ORAL DEBUG WARNING] Duplicate assistant audio gate release skipped",
        getOralDebugSnapshot({
          reason,
          frontendTurnId: currentTurnId,
        })
      );
      return;
    }

    lastAudioGateReleasedTurnIdRef.current = currentTurnId;

    debugLog(
      "[ORAL DEBUG] Assistant audio ended",
      getOralDebugSnapshot({
        reason,
        delayMs: POST_ASSISTANT_AUDIO_MIC_BUFFER_MS,
        frontendTurnId: currentTurnId,
      })
    );

    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    promptMuteTimerRef.current = setTimeout(() => {
      startupGuardRef.current = false;
      if (!firstTutorUtteranceDoneRef.current) {
        firstTutorUtteranceDoneRef.current = true;
        startupUnlockTimerRef.current = null;
      }

      if (sessionMetaRef.current.closed || isPracticePausedRef.current) {
        setMicrophoneTrackEnabled(false);
        promptMuteTimerRef.current = null;
        return;
      }

      const promptAction = pendingTutorPromptActionRef.current || "next_question";

      if (promptAction === "followup") {
        completeTutorPromptResponse(`${reason}: post-audio buffer elapsed`);
      } else {
        setMicrophoneTrackEnabled(false);
        completeTutorPromptResponse(`${reason}: post-audio buffer elapsed`);
      }

      promptMuteTimerRef.current = null;
    }, POST_ASSISTANT_AUDIO_MIC_BUFFER_MS);

    startupUnlockTimerRef.current = promptMuteTimerRef.current;
  };

  const fetchHtmlReport = async (
    sessionId,
    email,
    retries = 60,
    delayMs = 2000
  ) => {
    if (!sessionId || !email) return "";

    setReportStatus(isInterviewPractice ? "Fetching interview report..." : "Fetching graded report...");

    for (let i = 0; i < retries; i += 1) {
      let nextPollStatus = "";
      try {
        const res = await fetch(`/api/oral/report/${sessionId}`, {
          credentials: "include",
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-User-Email": email,
            "X-User-Id": userId,
            "X-Public-Paid-Oral": "true",
          },
        });

        const data = await res.json();
        const payload = getResponsePayload(data);

        if (isInsufficientOralAccessPayload(res.status, payload)) {
          showOralAccessNeeded(payload);
          appendLog("System", "Oral practice access is needed before the report can be generated.");
          return "";
        }

        if (!res.ok) {
          throw new Error(
            payload?.error || payload?.message || "Failed to fetch graded report."
          );
        }

        const reportStatusValue = String(
          payload?.report_status || payload?.report_generation_status || ""
        ).toLowerCase();
        const reportError =
          payload?.report_generation_error ||
          payload?.error ||
          payload?.message ||
          "";
        const nextReportUrl =
          payload?.html_report_sas_url ||
          payload?.html_report_url ||
          payload?.report_url ||
          "";

        if (reportStatusValue === "failed") {
          const msg = reportError
            ? `Report generation failed: ${reportError}`
            : "Report generation failed.";
          setReportUrl("");
          setReportStatus(msg);
          appendLog("System", msg);
          return "";
        }

        if (nextReportUrl || reportStatusValue === "ready") {
          if (!nextReportUrl) {
            setReportStatus("Graded report is ready, but no report URL was returned.");
            appendLog("System", "Graded report is ready, but no report URL was returned.");
            return "";
          }
          setReportUrl(nextReportUrl);
          setReportStatus(isInterviewPractice ? "Interview report ready." : "Graded report ready.");
          appendLog("System", "Graded HTML report is ready.");
          appendLog("System", nextReportUrl);
          return nextReportUrl;
        }

        if (reportStatusValue === "pending") {
          nextPollStatus = `Preparing graded report... (${i + 1}/${retries})`;
          setReportStatus(nextPollStatus);
        } else if (reportStatusValue === "not_started") {
          nextPollStatus = `Report generation is starting... (${i + 1}/${retries})`;
          setReportStatus(nextPollStatus);
        }
      } catch (err) {
        if (i === retries - 1) {
          const msg = err?.message || "Failed to fetch graded report.";
          appendLog("System", `Report fetch error: ${msg}`);
        }
      }

      if (!nextPollStatus) {
        setReportStatus(`Preparing graded report... (${i + 1}/${retries})`);
      }

      if (i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    setReportStatus("No graded report available yet.");
    appendLog(
      "System",
      "Session completed, but no graded HTML report URL was returned."
    );
    return "";
  };

  const languages =
    Array.isArray(topicsData?.languages) && topicsData.languages.length > 0
      ? topicsData.languages
      : DEFAULT_LANGUAGES;
  const standards = topicsData?.standards || topicsData?.activity_sets || [];
  const topicSets = topicsData?.topic_sets || [
    { topic_set_id: "default", topic_set_name: "Default Topics" },
  ];
  const interviewPacks = getInterviewPacks(topicsData);

  const selectedLanguage = useMemo(() => {
    return (
      languages.find(
        (language) => getSafeLanguageId(language) === selectedLanguageId
      ) ||
      null
    );
  }, [languages, selectedLanguageId]);

  const selectedTopicSet = useMemo(() => {
    return (
      topicSets.find((topicSet) => topicSet.topic_set_id === selectedTopicSetId) ||
      null
    );
  }, [topicSets, selectedTopicSetId]);
  const selectedInterviewPack = useMemo(
    () =>
      interviewPacks.find(
        (pack) => pack.topic_set_id === selectedTopicSetId
      ) || null,
    [interviewPacks, selectedTopicSetId]
  );

  const selectedStandardBlock = useMemo(() => {
    return (
      standards.find(
        (s) => (s.standard_id || s.activity_set_id) === selectedStandard
      ) || null
    );
  }, [standards, selectedStandard]);

  const availableTopics =
    selectedStandardBlock?.topics || selectedStandardBlock?.question_sets || [];

  const selectedTopic = useMemo(() => {
    return (
      availableTopics.find(
        (t) => (t.topic_id || t.question_set_id) === selectedTopicId
      ) || null
    );
  }, [availableTopics, selectedTopicId]);

  const stimulus = useMemo(() => {
    return getTopicStimulusMedia(selectedTopic, sessionInfo);
  }, [selectedTopic, sessionInfo]);

  const displayQuestions = useMemo(() => {
    const sessionQuestions = getOrderedQuestions(sessionInfo.questions || {});
    if (sessionQuestions.length > 0) {
      return sessionInfo.questions || {};
    }

    return selectedTopic?.questions || {};
  }, [selectedTopic, sessionInfo.questions]);

  const orderedDisplayQuestions = useMemo(() => {
    return getOrderedQuestions(displayQuestions);
  }, [displayQuestions]);

  function applyBootstrapTopics(payload, preferredLanguageId, preferredTopicSetId) {
    setTopicsData(payload);

    const payloadLanguages = getSafeLanguages(payload);
    const defaultLanguageId = getDefaultLanguageId(
      payload,
      preferredLanguageId || "english"
    );
    const defaultTopicSetId = isInterviewPractice
      ? payload?.topic_sets?.[0]?.topic_set_id || preferredTopicSetId || ""
      : payload?.topic_set_id ||
        payload?.topic_sets?.[0]?.topic_set_id ||
        preferredTopicSetId ||
        "default";
    const payloadStandards = payload?.standards || payload?.activity_sets || [];
    const firstStandard =
      payloadStandards[0]?.standard_id || payloadStandards[0]?.activity_set_id || "";
    const firstTopicBlock =
      payloadStandards[0]?.topics || payloadStandards[0]?.question_sets || [];
    const firstTopic = getFirstSelectableTopicId(firstTopicBlock);
    const languageStillValid = payloadLanguages.some(
      (language) => getSafeLanguageId(language) === selectedLanguageId
    );
    const topicSetStillValid = payload?.topic_sets?.some(
      (topicSet) => topicSet.topic_set_id === selectedTopicSetId
    );
    const standardStillValid = payloadStandards.some(
      (standard) =>
        (standard.standard_id || standard.activity_set_id) === selectedStandard
    );
    const nextStandard = standardStillValid ? selectedStandard : firstStandard;
    const topicsForStandard =
      (() => {
        const block = payloadStandards.find(
          (standard) =>
            (standard.standard_id || standard.activity_set_id) === nextStandard
        );
        return block?.topics || block?.question_sets || [];
      })();
    // Bootstrap access flags control display; start-session remains authoritative.
    const topicStillValid = topicsForStandard.some(
      (topic) =>
        getTopicId(topic) === selectedTopicId && isTopicSelectable(topic)
    );

    setSelectedLanguageId(
      languageStillValid ? selectedLanguageId : defaultLanguageId
    );
    setSelectedTopicSetId(
      topicSetStillValid ? selectedTopicSetId : defaultTopicSetId
    );
    setSelectedStandard(nextStandard);
    setSelectedTopicId(topicStillValid ? selectedTopicId : firstTopic);
  }

  async function loadPublicOralBootstrap(
    email,
    languageId = selectedLanguageId || "english",
    topicSetId = selectedTopicSetId || "default",
    options = {}
  ) {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const requestedLanguageId = languageId || "english";
    const requestedTopicSetId = topicSetId || "default";
    const requestKey = `${normalizedEmail}|${requestedLanguageId}|${requestedTopicSetId}`;

    if (!normalizedEmail) {
      setVerificationStatus("Please enter your email address.");
      return false;
    }

    if (!options.force && bootstrapRequestKeyRef.current === requestKey) {
      return true;
    }

    bootstrapRequestKeyRef.current = requestKey;
    setLoadingTopics(true);
    setTopicsError("");
    setVerificationStatus("Loading oral exam access...");
    setBootstrapErrorDetails(null);

    try {
      const res = await axios.get("/api/public/oral/bootstrap", {
        params: {
          practice_type: practiceType,
          email: normalizedEmail,
          language_id: requestedLanguageId,
          topic_set_id: requestedTopicSetId,
          level_id: isInterviewPractice ? "general" : undefined,
          standard: isInterviewPractice ? "general" : undefined,
        },
        ...PUBLIC_SESSION_CONFIG,
      });
      const payload = getResponsePayload(res.data || {});
      const bootstrappedUserId =
        payload.user_id || payload.data?.user_id || options.userId || "";
      const bootstrappedEmail =
        payload.user_email || payload.data?.user_email || normalizedEmail;
      const topicsPayload = payload.topics || payload.data?.topics || {};

      if (!payload.success && !payload.topics && !payload.data?.topics) {
        throw new Error("Failed to load oral exam access.");
      }
      if (!bootstrappedUserId) {
        throw new Error("Email verified, but no user ID was returned.");
      }

      if (bootstrappedEmail !== publicEmail) {
        emailVerificationUpdateRef.current = true;
      }

      setUserId(bootstrappedUserId);
      setPublicEmail(bootstrappedEmail);
      setStudentName(bootstrappedEmail);
      setStudentRegNumber(bootstrappedUserId);
      setResolvedClassContext({
        school_id: "public_use",
        teacher_id: "public_oral",
        year: new Date().getFullYear().toString(),
        student_class: "public_usage",
        language_id: requestedLanguageId,
      });
      applyBootstrapTopics(
        topicsPayload,
        requestedLanguageId,
        requestedTopicSetId
      );
      setEmailVerified(true);
      setEmailVerificationStatus(
        isInterviewPractice
          ? "Email verified. Choose a question pack to start."
          : "Email verified. Choose a topic to start."
      );
      setVerificationStatus(
        isInterviewPractice
          ? "Interview practice access loaded. Choose a question pack to start."
          : "Oral exam access loaded. Choose a topic to start."
      );
      return true;
    } catch (err) {
      bootstrapRequestKeyRef.current = "";
      const status = err?.response?.status;
      const data = err?.response?.data?.data || err?.response?.data || {};
      const reason = data?.reason || data?.error || data?.code || err?.message;

      setResolvedClassContext(null);
      setTopicsData(null);
      setSelectedStandard("");
      setSelectedTopicId("");

      if (isPublicSessionError(err) || status === 403 || reason === "email_not_verified") {
        clearPublicAuthentication("Your session has expired. Please verify your email again.");
        setVerificationStatus("Your session has expired. Please verify your email again.");
      } else if (isInsufficientOralAccessError(err)) {
        console.warn("Public oral bootstrap entitlement blocked:", {
          credit_balance: data?.credit_balance,
          entitlement_source: data?.entitlement_source,
          reason,
        });
        const knownUserId = options.userId || userId;
        if (knownUserId) {
          setUserId(knownUserId);
          setStudentRegNumber(knownUserId);
        }
        setStudentName(normalizedEmail);
        setEmailVerified(true);
        handleInsufficientOralAccessError(err, { reason });
      } else {
        setVerificationStatus("Failed to load oral exam access.");
        setEmailVerificationStatus("Failed to load oral exam access.");
      }

      return false;
    } finally {
      setLoadingTopics(false);
    }
  }

  const clearPublicAuthentication = (message = "") => {
    voiceStartup.reset();
    setPublicUser(null); setPublicBalances(null); setEmailVerified(false);
    setUserId(""); setStudentName(""); setStudentRegNumber("");
    setResolvedClassContext(null); setTopicsData(null); setBootstrapErrorDetails(null);
    setEmailCodeSent(false); setPublicEmailCode(""); setEmailVerificationStatus(message);
    bootstrapRequestKeyRef.current = "";
  };

  const applyPublicAccount = ({ user, balances }) => {
    const restoredUserId = user?.user_id || "";
    const restoredEmail = user?.masked_email || "";
    setPublicUser(user); setPublicBalances(balances); setUserId(restoredUserId);
    setStudentRegNumber(restoredUserId); setStudentName(restoredEmail);
    emailVerificationUpdateRef.current = true; setPublicEmail(restoredEmail);
    setEmailVerified(Boolean(restoredUserId));
  };

  const checkPublicSession = async () => {
    setCheckingPublicSession(true); setPublicSessionError("");
    try {
      const session = getPublicSessionPayload(await getPublicSession());
      if (!session.authenticated) {
        clearPublicAuthentication();
      } else {
        applyPublicAccount(session);
        await loadPublicOralBootstrap(
          session.user?.masked_email || "verified-user",
          selectedLanguageId || "english",
          selectedTopicSetId || "default",
          { force: true, userId: session.user?.user_id || "" }
        );
      }
    } catch (_) { setPublicSessionError("We could not check your account. Please try again."); }
    finally { setCheckingPublicSession(false); }
  };

  const handlePublicLogout = async () => {
    if (startRequestPendingRef.current || voiceStartup.busy) return;
    setLoggingOut(true);
    try {
      await logoutPublicSession();
      stopSession();
      clearPublicAuthentication(); setPublicEmail(""); setSessionStatus("Not started");
    } catch (_) { setEmailVerificationStatus("Logout failed. Please try again."); }
    finally { setLoggingOut(false); }
  };

  useEffect(() => {
    checkPublicSession();
    // Session restoration is a one-time page bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listeningStateRef.current = listeningState;
  }, [listeningState]);

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  useEffect(() => {
    studentCanRespondRef.current = studentCanRespond;
  }, [studentCanRespond]);

  useEffect(() => {
    if (isInterviewPractice) {
      return;
    }
    if (!emailVerified || !userId || !publicEmail.trim()) {
      return;
    }

    loadPublicOralBootstrap(
      publicEmail,
      selectedLanguageId || "english",
      selectedTopicSetId || "default"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguageId, selectedTopicSetId, isInterviewPractice]);

  useEffect(() => {
    if (!selectedStandardBlock) {
      setSelectedTopicId("");
      return;
    }

    const firstTopicId = getFirstSelectableTopicId(selectedStandardBlock.topics);
    const topicStillValid = selectedStandardBlock.topics?.some(
      (topic) =>
        getTopicId(topic) === selectedTopicId && isTopicSelectable(topic)
    );

    if (!topicStillValid) {
      setSelectedTopicId(firstTopicId);
    }
  }, [selectedStandardBlock, selectedTopicId]);

  useEffect(() => {
    if (emailVerificationUpdateRef.current) {
      emailVerificationUpdateRef.current = false;
      return;
    }

    setEmailVerified(false);
    setEmailCodeSent(false);
    setPublicEmailCode("");
    setUserId("");
    setStudentName("");
    setStudentRegNumber("");
    setResolvedClassContext(null);
    setVerificationStatus("");
    setBootstrapErrorDetails(null);
    bootstrapRequestKeyRef.current = "";
    setTopicsData(null);
    setTopicsError("");
    setSelectedStandard("");
    setSelectedTopicId("");
    setSessionStatus("Not started");
    setEmailVerificationStatus("");
  }, [publicEmail]);

  useEffect(() => {
    if (emailSendCooldown <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setEmailSendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [emailSendCooldown]);

  useEffect(() => {
    return () => {
      if (completionCleanupFallbackTimerRef.current) {
        clearTimeout(completionCleanupFallbackTimerRef.current);
        completionCleanupFallbackTimerRef.current = null;
      }
      clearResponseActiveDiagnosticTimer();

      if (sessionRef.current) {
        try {
          sessionRef.current.close();
        } catch (_) {}
      }

      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
        microphoneStreamRef.current = null;
      }
    };
  }, []);

  const clearCompletionCleanupFallbackTimer = (reason) => {
    if (!completionCleanupFallbackTimerRef.current) {
      return;
    }

    clearTimeout(completionCleanupFallbackTimerRef.current);
    completionCleanupFallbackTimerRef.current = null;
    debugLog(
      "[ORAL DEBUG] Completion fallback timer cleared",
      getOralDebugSnapshot({ reason })
    );
  };

  const startCompletionCleanupFallbackTimer = (sessionKey, reason) => {
    if (!sessionKey) {
      return;
    }

    if (completionCleanupFallbackTimerRef.current) {
      debugLog(
        "[ORAL DEBUG] Duplicate completion fallback ignored",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      return;
    }

    completionCleanupFallbackTimerRef.current = setTimeout(() => {
      completionCleanupFallbackTimerRef.current = null;

      if (sessionKey !== activeSessionKeyRef.current) {
        debugLog(
          "[ORAL DEBUG] Stale completion fallback ignored",
          getOralDebugSnapshot({
            reason,
            eventSessionKey: sessionKey,
            activeSessionKey: activeSessionKeyRef.current || "",
          })
        );
        return;
      }

      if (completionCleanupStartedRef.current) {
        debugLog(
          "[ORAL DEBUG] Duplicate completion fallback ignored",
          getOralDebugSnapshot({ reason, sessionKey })
        );
        return;
      }

      debugLog(
        "[ORAL DEBUG] Completion fallback cleanup triggered",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      runCompletionCleanupAfterFinalAudio(sessionKey, "completion fallback timeout");
    }, COMPLETION_CLEANUP_FALLBACK_MS);

    debugLog(
      "[ORAL DEBUG] Completion fallback timer started",
      getOralDebugSnapshot({
        reason,
        sessionKey,
        timeoutMs: COMPLETION_CLEANUP_FALLBACK_MS,
      })
    );
  };

  const stopSession = (options = {}) => {
    const {
      reason = "manual",
      statusAfter = "Disconnected",
      preserveCompletionState = false,
    } = options;
    if (reason !== "voice startup failed") voiceStartup.reset();
    debugLog(
      "[ORAL DEBUG] Full realtime cleanup started",
      getOralDebugSnapshot({ reason })
    );

    activeSessionKeyRef.current = "";
    if (sessionRef.current) {
      intentionalCloseRef.current = true;
      try {
        sessionRef.current.close();
      } catch (_) {}
    }

    sessionRef.current = null;
    sessionMetaRef.current = {
      sessionId: "",
      currentQuestionIndex: 1,
      userEmail: "",
      userId: "",
      lastSpokenPrompt: "",
      closed: true,
    };

    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
      microphoneStreamRef.current = null;
    }

    if (startupUnlockTimerRef.current) {
      clearTimeout(startupUnlockTimerRef.current);
      startupUnlockTimerRef.current = null;
    }

    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    if (completionCloseTimerRef.current) {
      clearTimeout(completionCloseTimerRef.current);
      completionCloseTimerRef.current = null;
    }
    clearCompletionCleanupFallbackTimer(`${reason}: stopSession`);

    activeSessionKeyRef.current = "";
    completionCleanupSessionKeyRef.current = "";
    completionCleanupStartedRef.current = false;
    startupGuardRef.current = false;
    firstTutorUtteranceDoneRef.current = false;
    micMutedRef.current = false;
    realtimeResponseActiveRef.current = false;
    realtimeResponseRequestPendingRef.current = false;
    realtimeResponseRequestSourceRef.current = "";
    activeRealtimeResponseIdRef.current = "";
    assistantAudioActiveRef.current = false;
    clearResponseActiveDiagnosticTimer();
    isPracticePausedRef.current = false;
    prePauseListeningStateRef.current = "ready";
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(false);
    pendingTutorPromptActionRef.current = "next_question";
    readyButtonPressedAtRef.current = "";
    ignoredTranscriptBeforeReadyRef.current = 0;
    frontendTurnIdRef.current = 0;
    lastAudioGateReleasedTurnIdRef.current = null;

    setIsConnected(false);
    setRealtimeCleanupInProgress(false);
    setStudentCanRespond(false);
    setReadyButtonPressedAt("");
    setIgnoredTranscriptBeforeReady(0);
    setListeningState("muted");
    setSessionStatus(statusAfter);
    debugLog(
      "[ORAL DEBUG] Full realtime cleanup completed",
      getOralDebugSnapshot({ reason, statusAfter, preserveCompletionState })
    );
  };

  const hasStaleRealtimeResources = () =>
    Boolean(
      sessionRef.current ||
        microphoneStreamRef.current ||
        startupUnlockTimerRef.current ||
        promptMuteTimerRef.current ||
        completionCloseTimerRef.current ||
        completionCleanupFallbackTimerRef.current ||
        activeSessionKeyRef.current ||
        completionCleanupSessionKeyRef.current ||
        completionCleanupStartedRef.current ||
        micMutedRef.current ||
        realtimeResponseActiveRef.current ||
        realtimeResponseRequestPendingRef.current ||
        activeRealtimeResponseIdRef.current ||
        assistantAudioActiveRef.current ||
        responseActiveDiagnosticTimerRef.current ||
        studentCanRespondRef.current ||
        studentReadyToAnswerRef.current ||
        waitingForReadyPressRef.current ||
        frontendTurnIdRef.current ||
        lastAudioGateReleasedTurnIdRef.current !== null
    );

  const runCompletionCleanupAfterFinalAudio = (sessionKey, reason) => {
    if (!sessionKey || sessionKey !== activeSessionKeyRef.current) {
      debugLog(
        "[ORAL DEBUG] Duplicate cleanup event ignored",
        getOralDebugSnapshot({
          reason,
          eventSessionKey: sessionKey || "",
          activeSessionKey: activeSessionKeyRef.current || "",
        })
      );
      return;
    }
    clearCompletionCleanupFallbackTimer(`${reason}: final audio cleanup`);

    if (
      completionCleanupStartedRef.current ||
      completionCleanupSessionKeyRef.current === sessionKey
    ) {
      debugLog(
        "[ORAL DEBUG] Duplicate cleanup event ignored",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      return;
    }

    completionCleanupStartedRef.current = true;
    completionCleanupSessionKeyRef.current = sessionKey;
    setRealtimeCleanupInProgress(true);
    setStudentCanRespond(false);
    setWaitingForReadyPressValue(false);
    setListeningState("muted");
    setSessionStatus("Completed. Cleaning up voice session...");

    debugLog(
      "[ORAL DEBUG] Final completion audio stopped",
      getOralDebugSnapshot({ reason, sessionKey })
    );

    if (completionCloseTimerRef.current) {
      clearTimeout(completionCloseTimerRef.current);
    }

    completionCloseTimerRef.current = setTimeout(() => {
      stopSession({
        reason: `completion cleanup: ${reason}`,
        statusAfter: "Completed",
        preserveCompletionState: true,
      });
      debugLog(
        "[ORAL DEBUG] Report polling continuing after realtime cleanup",
        getOralDebugSnapshot({
          reason,
          reportStatus,
          reportUrl,
        })
      );
    }, 250);
  };

  const buildEvaluateTurnTool = () =>
    tool({
      name: "evaluate_oral_turn",
      description:
        "Call this after every student answer. Send the student's spoken answer transcript to the backend. The backend decides whether to follow up, ask the next question, or complete the session. You must then speak the returned spoken_prompt exactly.",
      parameters: z.object({
        transcript: z
          .string()
          .describe("The student's spoken answer transcript."),
      }),
      async execute({ transcript }) {
        const meta = sessionMetaRef.current;
        const transcriptText = String(transcript || "");
        debugLog(
          "[ORAL DEBUG] evaluate_oral_turn execute",
          getOralDebugSnapshot({
            reason: "evaluate_oral_turn execute",
            transcriptLength: transcriptText.length,
            transcriptPreview: transcriptText.slice(0, 200),
          })
        );

        if (isPracticePausedRef.current) {
          appendLog("System", "Evaluation ignored because practice is paused.");
          debugLog(
            "[ORAL DEBUG] Evaluation ignored while paused",
            getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
            })
          );
          return {
            action: "paused",
            spoken_prompt: "",
            current_question_index: meta.currentQuestionIndex || 1,
            status: "paused",
            instruction:
              "Do not speak. Do not call any tools until the student resumes.",
          };
        }

        if (startupGuardRef.current) {
          appendLog("System", "Ignoring input during startup guard.");
          return {
            action: "followup",
            spoken_prompt: "",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "awaiting_answer",
            instruction:
              "Do not speak. Ignore this input and wait for the next real student answer.",
          };
        }

        if (!studentReadyToAnswerRef.current) {
          const ignoredCount = ignoredTranscriptBeforeReadyRef.current + 1;
          ignoredTranscriptBeforeReadyRef.current = ignoredCount;
          setIgnoredTranscriptBeforeReady(ignoredCount);
          appendLog("System", "Transcript ignored. Student has not pressed Ready to Answer.");
          debugLog(
            "[ORAL DEBUG] Evaluation ignored before Ready to Answer",
            getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
              ignoredTranscriptBeforeReady: ignoredCount,
            })
          );
          return {
            action: "awaiting_ready",
            spoken_prompt: "",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "thinking",
            instruction:
              "Do not speak. Ignore this transcript and wait until the student presses Ready to Answer.",
          };
        }

        if (!meta.sessionId) {
          return {
            action: "error",
            spoken_prompt:
              "The session is not ready. Please start a new oral practice.",
            current_question_index: 1,
            status: "error",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }

        studentReadyToAnswerRef.current = false;
        studentCanRespondRef.current = false;
        setWaitingForReadyPressValue(false);
        setStudentCanRespond(false);
        appendLog("Student", transcript);

        if (sessionMetaRef.current.closed) {
          return {
            action: "completed",
            spoken_prompt: "This oral practice session is already completed.",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "completed",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }

        try {
          listeningStateRef.current = "processing";
          setListeningState("processing");
          appendLog(
            "System",
            `Sending transcript to backend for question ${meta.currentQuestionIndex}`
          );
          debugLog(
            "[ORAL DEBUG] /api/oral/process-turn POST",
            getOralDebugSnapshot({
              questionIndex: meta.currentQuestionIndex,
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
              transcriptFull: transcript || "",
              reason: "process-turn POST",
            })
          );

          const res = await axios.post("/api/oral/process-turn", {
            practice_type: practiceType,
            session_id: meta.sessionId,
            question_index: meta.currentQuestionIndex,
            transcript,
            user_email: meta.userEmail,
          }, PUBLIC_SESSION_CONFIG);

          const result = res.data?.data || res.data || {};

          const nextQuestionIndex =
            result.current_question_index || meta.currentQuestionIndex || 1;
          const spokenPrompt = result.spoken_prompt || "";
          // Deprecated timing value retained only for backend/debug payloads.
          // Do not use prompt_mute_ms for mic gating; use audio output events.
          const promptMuteMs = Number(result.prompt_mute_ms) || 0;
          const nextStatus = result.status || "awaiting_answer";
          const nextAction = result.action || "followup";
          pendingTutorPromptActionRef.current =
            nextStatus === "completed" ? "completed" : nextAction;

          appendLog(
            "System",
            `Backend action: ${result.action || "unknown"}, status: ${
              result.status || "unknown"
            }`
          );
          debugLog("[ORAL DEBUG] Backend response received", {
            backendAction: result.action || "unknown",
            backendStatus: result.status || "unknown",
            spokenPrompt: spokenPrompt,
            nextQuestionIndex,
          });

          sessionMetaRef.current = {
            ...sessionMetaRef.current,
            currentQuestionIndex: nextQuestionIndex,
            lastSpokenPrompt: spokenPrompt,
            closed: nextStatus === "completed",
          };

          setSessionInfo((prev) => ({
            ...prev,
            currentQuestionIndex: nextQuestionIndex,
            status: nextStatus,
            spokenPrompt: spokenPrompt,
          }));

          setSessionStatus(
            result.status === "completed"
              ? "Completed. Finishing final message..."
              : `Connected — ${result.action || "awaiting_answer"}`
          );

          if (nextStatus === "completed") {
            setRealtimeCleanupInProgress(true);
            studentReadyToAnswerRef.current = false;
            studentCanRespondRef.current = false;
            setWaitingForReadyPressValue(false);
            setStudentCanRespond(false);
            setListeningState("muted");
            sessionMetaRef.current = {
              ...sessionMetaRef.current,
              currentQuestionIndex: nextQuestionIndex,
              lastSpokenPrompt: spokenPrompt,
              closed: true,
            };
            debugLog(
              "[ORAL DEBUG] Topic marked completed",
              getOralDebugSnapshot({
                backendAction: result.action || "unknown",
                backendStatus: result.status || "unknown",
                sessionKey: activeSessionKeyRef.current,
              })
            );
            debugLog(
              "[ORAL DEBUG] Waiting for final completion audio",
              getOralDebugSnapshot({
                sessionKey: activeSessionKeyRef.current,
                hasSpokenPrompt: Boolean(spokenPrompt),
              })
            );
            startCompletionCleanupFallbackTimer(
              activeSessionKeyRef.current,
              "backend completed"
            );
            fetchHtmlReport(meta.sessionId, meta.userEmail);
          }

          appendLog("Tutor", spokenPrompt);

          if (spokenPrompt && !isPracticePausedRef.current) {
            markPromptStarted("backend spoken prompt", spokenPrompt);
            setListeningState("teacher_speaking");
            waitForAssistantAudioEnd(nextStatus);
          } else if (nextStatus === "completed") {
            getPublicSession().then((response) => {
              const account = getPublicSessionPayload(response);
              if (account.authenticated) setPublicBalances(account.balances);
            }).catch(() => {});
            runCompletionCleanupAfterFinalAudio(
              activeSessionKeyRef.current,
              "completed without spoken prompt"
            );
          } else if (nextStatus !== "completed" && !isPracticePausedRef.current) {
            completeTutorPromptResponse("backend response without spoken prompt");
          }

          return {
            action: nextAction,
            spoken_prompt: spokenPrompt,
            prompt_mute_ms: promptMuteMs,
            current_question_index: nextQuestionIndex,
            status: nextStatus,
            instruction:
              "Speak the spoken_prompt exactly. Do not change wording. Do not invent a new question.",
          };
        } catch (err) {
          if (isPublicSessionError(err)) {
            appendLog("System", "Your session has expired.");
            stopSession();
            clearPublicAuthentication("Your session has expired. Please verify your email again.");
            return { action: "error", spoken_prompt: "Your session has expired. Please verify your email again.", status: "error" };
          }
          if (isInsufficientOralAccessError(err)) {
            appendLog("System", "Oral practice access needed.");
            stopSession();
            handleInsufficientOralAccessError(err);

            return {
              action: "error",
              spoken_prompt:
                "Your oral practice access has run out. Please top up or renew access to continue.",
              current_question_index: meta.currentQuestionIndex,
              status: "error",
              instruction:
                "Speak the spoken_prompt exactly and do not add anything else.",
            };
          }

          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Failed to process oral turn.";

          appendLog("System", `Backend error: ${msg}`);
          if (!isPracticePausedRef.current) {
            setThinkingAfterTutorPrompt("backend error");
          }

          return {
            action: "error",
            spoken_prompt:
              "There was a temporary problem processing the answer. Please try again.",
            current_question_index: meta.currentQuestionIndex,
            status: "error",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }
      },
    });

  const refreshSelectedTopicDisplay = async () => {
    if (isInterviewPractice) {
      await loadPublicOralBootstrap(
        publicEmail,
        "english",
        selectedTopicSetId || "default",
        { force: true, userId }
      );
      return;
    }

    const standardsList = topicsData?.standards || [];
    const standardBlock =
      standardsList.find((s) => s.standard_id === selectedStandard) || null;
    const topic =
      standardBlock?.topics?.find((t) => t.topic_id === selectedTopicId) || null;

    setSessionInfo((prev) => ({
      ...prev,
      questions: topic?.questions || {},
    }));
  };

  const chooseFirstFreeTrialTopic = () => {
    const promotionalTopic = availableTopics.find(
      (topic) =>
        topic?.access_badge === "Free trial" && isTopicSelectable(topic)
    );
    if (promotionalTopic) {
      setSelectedTopicId(getTopicId(promotionalTopic));
      setBootstrapErrorDetails(null);
      setSessionStatus("Free trial topic selected.");
    }
  };

  const handleSendEmailCode = async () => {
    const normalizedEmail = publicEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    setSendingEmailCode(true);
    setEmailVerified(false);
    setBootstrapErrorDetails(null);
    bootstrapRequestKeyRef.current = "";
    setEmailVerificationStatus("Sending email verification code...");

    try {
      await axios.post("/api/public/oral/send-email-code", {
        email: normalizedEmail,
      });
      setEmailCodeSent(true);
      setEmailSendCooldown(30);
      setEmailVerificationStatus(
        `Verification code sent to ${normalizedEmail}.`
      );
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send email verification code.";
      setEmailVerificationStatus(msg);
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const normalizedEmail = publicEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    if (!publicEmailCode.trim()) {
      setEmailVerificationStatus("Please enter the email verification code.");
      return;
    }

    setVerifyingEmailCode(true);
    setEmailVerificationStatus("Verifying email code...");

    try {
      const res = await axios.post("/api/public/oral/verify-email-code", {
        email: normalizedEmail,
        code: publicEmailCode.trim(),
        marketing_email_consent: false,
      }, PUBLIC_SESSION_CONFIG);

      const verifiedUserId =
        res.data?.user_id || res.data?.data?.user_id || "";
      const verifiedEmail =
        res.data?.user_email ||
        res.data?.data?.user_email ||
        normalizedEmail;

      if (!verifiedUserId) {
        throw new Error("Email verified, but no user ID was returned.");
      }

      if (verifiedEmail !== publicEmail) {
        emailVerificationUpdateRef.current = true;
      }
      setUserId(verifiedUserId);
      setPublicEmail(verifiedEmail);
      setStudentName(verifiedEmail);
      setStudentRegNumber(verifiedUserId);
      setEmailVerificationStatus("Email verified. Loading oral exam access...");

      const loaded = await loadPublicOralBootstrap(
        verifiedEmail,
        selectedLanguageId || "english",
        selectedTopicSetId || "default",
        { force: true, userId: verifiedUserId }
      );
      if (loaded) {
        const session = getPublicSessionPayload(await getPublicSession());
        if (session.authenticated) applyPublicAccount(session);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to verify email code.";
      setEmailVerified(false);
      setUserId("");
      setStudentName("");
      setStudentRegNumber("");
      setResolvedClassContext(null);
      setBootstrapErrorDetails(null);
      setEmailVerificationStatus(msg);
    } finally {
      setVerifyingEmailCode(false);
    }
  };

  const startVoiceSession = async () => {
    if (startRequestPendingRef.current || voiceStartup.busy || voiceStartup.error || isConnected) return;
    const normalizedEmail = publicEmail.trim().toLowerCase();

    if (!emailVerified) {
      setSessionStatus("Please verify your email address first.");
      return;
    }

    if (!userId) {
      setSessionStatus("Please verify your email address again to get your user ID.");
      return;
    }

    if (!resolvedClassContext) {
      setSessionStatus("Please verify your email address again.");
      return;
    }

    if (!isInterviewPractice && !selectedStandard) {
      setSessionStatus("Please select a standard.");
      return;
    }

    if (isInterviewPractice ? !selectedTopicSetId : !selectedTopicId) {
      setSessionStatus(
        isInterviewPractice
          ? "Please select a question pack."
          : "Please select a topic."
      );
      return;
    }

    if (!isInterviewPractice && !isTopicSelectable(selectedTopic)) {
      setSessionStatus(getTopicLockMessage(selectedTopic));
      return;
    }

    if (!normalizedEmail) {
      setSessionStatus("Please enter your email address.");
      return;
    }

    if (hasStaleRealtimeResources()) {
      debugLog(
        "[ORAL DEBUG] Stale session found before new session start",
        getOralDebugSnapshot({
          hasSession: Boolean(sessionRef.current),
          hasMicrophoneStream: Boolean(microphoneStreamRef.current),
          hasCompletionTimer: Boolean(completionCloseTimerRef.current),
          activeSessionKey: activeSessionKeyRef.current || "",
        })
      );
      stopSession({
        reason: "defensive cleanup before new session start",
        statusAfter: "Starting session...",
      });
      debugLog(
        "[ORAL DEBUG] Defensive cleanup completed",
        getOralDebugSnapshot({ reason: "before new session start" })
      );
    }

    startRequestPendingRef.current = true;
    setStartingSession(true);
    setRealtimeCleanupInProgress(false);
    intentionalCloseRef.current = false;
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(false);
    pendingTutorPromptActionRef.current = "next_question";
    readyButtonPressedAtRef.current = "";
    ignoredTranscriptBeforeReadyRef.current = 0;
    frontendTurnIdRef.current = 0;
    lastAudioGateReleasedTurnIdRef.current = null;
    setStudentCanRespond(false);
    setListeningState("muted");
    isPracticePausedRef.current = false;
    prePauseListeningStateRef.current = "ready";
    setReadyButtonPressedAt("");
    setIgnoredTranscriptBeforeReady(0);
    setSessionStatus("Starting session...");
    setEventLog([]);
    setReportUrl("");
    setReportStatus("");

    try {
      try {
        const emailVerifiedRes = await axios.get("/api/public/oral/email-verified", {
          params: { email: normalizedEmail },
          ...PUBLIC_SESSION_CONFIG,
        });
        const isStillVerified =
          emailVerifiedRes.data?.data?.verified ??
          emailVerifiedRes.data?.verified ??
          false;

        if (!isStillVerified) {
          setEmailVerified(false);
          setStudentName("");
          setSessionStatus("Please verify your email address again.");
          return;
        }
      } catch (err) {
        const verificationMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Please verify your email address again.";
        setSessionStatus(verificationMsg);
        return;
      }

      // 1) Create backend session
      const baseStartPayload = {
        user_id: userId,
        user_email: normalizedEmail,
        topic_id: selectedTopicId,
        language_id: selectedLanguageId || "english",
        topic_set_id: selectedTopicSetId || "default",
        school_id: resolvedClassContext.school_id,
        teacher_id: resolvedClassContext.teacher_id,
        year: resolvedClassContext.year,
        student_class: resolvedClassContext.student_class,
        student_name: normalizedEmail,
        student_reg_number: userId,
        student_verification_code: "",
        is_public_paid_oral: true,
        pause_duration_mode: selectedPauseDurationMode,
      };
      const startPayload = isInterviewPractice
        ? buildInterviewStartPayload(baseStartPayload, selectedTopicSetId)
        : {
            ...baseStartPayload,
            standard: selectedStandard,
            practice_type: practiceType,
          };
      console.log("Starting oral session with payload:", startPayload);
      const startRes = await axios.post("/api/oral/start-session", startPayload, PUBLIC_SESSION_CONFIG);
      const startResponsePayload = getResponsePayload(startRes.data || {});

      if (
        isInsufficientOralAccessPayload(
          startRes.status,
          startResponsePayload
        )
      ) {
        showOralAccessNeeded(startResponsePayload);
        return;
      }

      const sessionPayload = startResponsePayload;
      await voiceStartup.start(async (attempt) => {
        intentionalCloseRef.current = false;
        setRealtimeCleanupInProgress(false);
        setListeningState("muted");
        const sessionUserContext = sessionPayload.user_context || {};
        const sessionUserEmail =
          sessionUserContext.user_email ||
          sessionPayload.user_email ||
          normalizedEmail;
        const openingPrompt = sessionPayload.spoken_prompt || "";

        setSessionInfo({
          sessionId: sessionPayload.session_id || "",
          status: sessionPayload.status || "",
          languageId: sessionPayload.language_id || selectedLanguageId || "english",
          standard: sessionPayload.standard || "",
          topicSetId: sessionPayload.topic_set_id || selectedTopicSetId || "default",
          topicId: sessionPayload.topic_id || "",
          topicTitle: sessionPayload.topic_title || "",
          pictureStimulus: sessionPayload.picture_stimulus || "",
          pictureStimulusUrl: sessionPayload.picture_stimulus_url || "",
          stimulusMedia: sessionPayload.stimulus_media || {
            media_type: sessionPayload.picture_stimulus_url ? "image" : "none",
            source_type: "legacy_picture_url",
            display_url: sessionPayload.picture_stimulus_url || "",
            video_url: "",
            blob_name: "",
            description: sessionPayload.picture_stimulus || "",
          },
          questions: sessionPayload.questions || {},
          questionCount:
            sessionPayload.question_count ||
            getOrderedQuestions(sessionPayload.questions || {}).length,
          currentQuestionIndex: sessionPayload.current_question_index || 1,
          spokenPrompt: openingPrompt,
        });

        // 2) Load realtime agent configuration from backend
        setSessionStatus("Fetching Realtime config...");
        const realtimeConfigRes = await attempt.wait("loading_configuration", () => axios.get("/api/oral/realtime-config", {
          params: {
            language_id: selectedLanguageId || "english",
            practice_type: practiceType,
            pause_duration_mode: selectedPauseDurationMode,
          },
          ...PUBLIC_SESSION_CONFIG,
        }), CONNECTION_TIMEOUT_MS);
        const realtimeConfig = realtimeConfigRes.data?.data || {};
        const backendAudioConfig = realtimeConfig.audio || {};
        const backendInputConfig = backendAudioConfig.input || {};
        const backendTurnDetection =
          backendInputConfig.turn_detection ||
          backendInputConfig.turnDetection ||
          {};
        const sdkTurnDetection =
          mapBackendTurnDetectionToSdk(backendTurnDetection);
        debugLog("[ORAL DEBUG] Realtime turn detection config from backend", {
          selectedPauseDurationMode,
          backendTurnDetection,
          sdkTurnDetection,
        });

        if (
          !realtimeConfig.agent_name ||
          !realtimeConfig.instructions ||
          !realtimeConfig.voice ||
          !realtimeConfig.model
        ) {
          throw new Error("Realtime config missing required fields");
        }

        // 3) Get ephemeral realtime token
        setSessionStatus("Fetching Realtime token...");
        debugLog(
          "[ORAL DEBUG] Fresh realtime token requested",
          getOralDebugSnapshot({ practiceType })
        );
        const tokenRes = await attempt.wait("requesting_token", () => axios.get("/api/realtime-token", {
          params: {
            practice_type: practiceType,
            pause_duration_mode: selectedPauseDurationMode,
          },
          ...PUBLIC_SESSION_CONFIG,
        }), CONNECTION_TIMEOUT_MS);
        const clientSecret =
          tokenRes.data?.data?.client_secret || tokenRes.data?.client_secret || "";

        if (!clientSecret) {
          throw new Error("Realtime token missing client_secret");
        }

        // 4) Create agent with local tool
        const evaluateOralTurnTool = buildEvaluateTurnTool();

        const agent = new RealtimeAgent({
          name: realtimeConfig.agent_name,
          instructions: realtimeConfig.instructions,
          voice: realtimeConfig.voice,
          tools: [evaluateOralTurnTool],
        });

        const microphoneStream = await attempt.wait("opening_microphone", () => navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }), MICROPHONE_TIMEOUT_MS, (stream) => stream.getTracks().forEach((track) => track.stop()));

        microphoneStreamRef.current = microphoneStream;
        setMicrophoneTrackEnabled(false);
        debugLog("[ORAL DEBUG] Mic disabled", {
          reason: "manual start; disabled until student can respond",
        });

        const realtimeSession = new RealtimeSession(agent, {
          model: realtimeConfig.model,
          transport: new OpenAIRealtimeWebRTC({
            mediaStream: microphoneStream,
          }),
          config: {
            audio: {
              input: {
                turnDetection: sdkTurnDetection,
              },
            },
          },
        });
        const sessionKey = `${sessionPayload.session_id || "oral"}:${Date.now()}`;
        activeSessionKeyRef.current = sessionKey;
        completionCleanupSessionKeyRef.current = "";
        completionCleanupStartedRef.current = false;
        realtimeResponseRequestPendingRef.current = false;
        realtimeResponseActiveRef.current = false;
        realtimeResponseRequestSourceRef.current = "";
        activeRealtimeResponseIdRef.current = "";
        assistantAudioActiveRef.current = false;
        clearResponseActiveDiagnosticTimer();
        debugLog(
          "[ORAL DEBUG] New realtime session created",
          getOralDebugSnapshot({
            sessionKey,
            backendSessionId: sessionPayload.session_id || "",
          })
        );

        realtimeSession.on("event", (event) => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale realtime event",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
              type: event?.type || "",
            });
            return;
          }
          const eventType = event?.type || "";
          const trackedTypes = new Set([
            "response.created",
            "input_audio_buffer.speech_started",
            "input_audio_buffer.speech_stopped",
            "input_audio_buffer.committed",
            "conversation.item.input_audio_transcription.completed",
            "response.audio_transcript.done",
            "response.done",
            "response.cancelled",
            "response.failed",
            "error",
          ]);

          if (!trackedTypes.has(eventType)) {
            return;
          }

          const transcriptText = String(
            event?.transcript || event?.text || event?.delta || ""
          );
          const responseId = event?.response_id || event?.response?.id || "";

          debugLog("[ORAL DEBUG] Realtime event", {
            ...getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
            }),
            type: eventType,
            transcript: event?.transcript || event?.text || "",
            itemId: event?.item_id || event?.item?.id || "",
            responseId,
            audioStartMs: event?.audio_start_ms,
            audioEndMs: event?.audio_end_ms,
            payload: {
              error: event?.error || null,
              delta: event?.delta || "",
              status: event?.status || "",
            },
          });

          if (eventType === "response.created") {
            realtimeResponseRequestPendingRef.current = false;
            realtimeResponseActiveRef.current = true;
            activeRealtimeResponseIdRef.current = responseId;
          }

          if (
            eventType === "response.done" ||
            eventType === "response.cancelled" ||
            eventType === "response.failed"
          ) {
            if (
              activeRealtimeResponseIdRef.current &&
              responseId &&
              responseId !== activeRealtimeResponseIdRef.current
            ) {
              debugLog("[ORAL DEBUG] Ignored stale realtime response terminal event", {
                type: eventType,
                responseId,
                activeResponseId: activeRealtimeResponseIdRef.current,
              });
              return;
            }
            realtimeResponseRequestPendingRef.current = false;
            activeRealtimeResponseIdRef.current = "";
            maybeClearRealtimeResponseActive(eventType);
            debugLog("[ORAL DEBUG] Realtime response terminal event", {
              ...getOralDebugSnapshot({
                transcriptLength: transcriptText.length,
                transcriptPreview: transcriptText.slice(0, 200),
              }),
              type: eventType,
              responseId,
              assistantAudioActive: assistantAudioActiveRef.current,
            });
          }

          if (eventType === "error") {
            const { code: eventErrorCode, message: eventErrorMessage } =
              normalizeRealtimeError(event);
            debugLog("[ORAL DEBUG] Realtime event", {
              ...getOralDebugSnapshot({
                transcriptLength: transcriptText.length,
                transcriptPreview: transcriptText.slice(0, 200),
              }),
              type: eventType,
              errorCode: eventErrorCode,
              errorMessage: eventErrorMessage,
              rawError: event?.error || null,
            });
            if (eventErrorCode === "conversation_already_has_active_response") {
              const pendingBeforeClear = realtimeResponseRequestPendingRef.current;
              realtimeResponseRequestPendingRef.current = false;
              maybeClearRealtimeResponseActive("duplicate response request rejected");
              debugLog("[ORAL DEBUG] Recoverable realtime duplicate response rejected", {
                errorCode: eventErrorCode,
                errorMessage: eventErrorMessage,
                responseRequestPendingBeforeClear: pendingBeforeClear,
                responseRequestPendingAfterClear: realtimeResponseRequestPendingRef.current,
                activeResponseId: activeRealtimeResponseIdRef.current,
                assistantAudioActive: assistantAudioActiveRef.current,
                sessionKey,
                requestSource: realtimeResponseRequestSourceRef.current,
              });
              return;
            }
            realtimeResponseRequestPendingRef.current = false;
            activeRealtimeResponseIdRef.current = "";
            assistantAudioActiveRef.current = false;
            maybeClearRealtimeResponseActive("realtime error event");
          }
        });

        realtimeSession.on("error", (err) => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale realtime error",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          if (intentionalCloseRef.current || sessionMetaRef.current.closed) {
            console.warn("Ignoring Realtime error during intentional shutdown:", err);
            return;
          }
          const { code: errorCode, message: errorMessage } =
            normalizeRealtimeError(err);

          debugLog("[ORAL DEBUG] Realtime event", {
            ...getOralDebugSnapshot(),
            type: "error",
            payload: {
              message: errorMessage || String(err || ""),
              code: errorCode,
            },
          });
          if (errorCode === "conversation_already_has_active_response") {
            const pendingBeforeClear = realtimeResponseRequestPendingRef.current;
            realtimeResponseRequestPendingRef.current = false;
            maybeClearRealtimeResponseActive("duplicate response request rejected");
            debugLog("[ORAL DEBUG] Recoverable realtime duplicate response rejected", {
              errorCode,
              errorMessage,
              responseRequestPendingBeforeClear: pendingBeforeClear,
              responseRequestPendingAfterClear: realtimeResponseRequestPendingRef.current,
              activeResponseId: activeRealtimeResponseIdRef.current,
              assistantAudioActive: assistantAudioActiveRef.current,
              sessionKey,
              requestSource: realtimeResponseRequestSourceRef.current,
            });
            return;
          }
          realtimeResponseRequestPendingRef.current = false;
          activeRealtimeResponseIdRef.current = "";
          assistantAudioActiveRef.current = false;
          maybeClearRealtimeResponseActive("realtime error");
          console.error("Realtime error:", err);
          appendLog("System", "Realtime error occurred.");
          if (waitingForReadyPressRef.current) {
            debugLog("[ORAL DEBUG] Realtime error ignored while waiting for Ready press", {
              errorCode,
              errorMessage: errorMessage || String(err || ""),
            });
            return;
          }
          setListeningState("muted");
          setSessionStatus("Realtime error");
        });

        realtimeSession.on("audio_start", () => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale audio_start",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          if (promptMuteTimerRef.current) {
            clearTimeout(promptMuteTimerRef.current);
            promptMuteTimerRef.current = null;
          }
          assistantAudioActiveRef.current = true;
          realtimeResponseActiveRef.current = true;
          disableMicrophoneTrack("assistant audio output started");
          setWaitingForReadyPressValue(false);
          if (isPracticePausedRef.current) {
            prePauseListeningStateRef.current = "teacher_speaking";
            return;
          }
          studentReadyToAnswerRef.current = false;
          studentCanRespondRef.current = false;
          listeningStateRef.current = "teacher_speaking";
          sessionStatusRef.current = "Tutor speaking...";
          setStudentCanRespond(false);
          setListeningState("teacher_speaking");
          setSessionStatus("Tutor speaking...");
        });

        realtimeSession.on("audio_stopped", () => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale audio_stopped",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          if (sessionMetaRef.current.closed) {
            assistantAudioActiveRef.current = false;
            setMicrophoneTrackEnabled(false);
            setStudentCanRespond(false);
            setListeningState("muted");
            setSessionStatus("Completed. Cleaning up voice session...");
            setWaitingForReadyPressValue(false);
            runCompletionCleanupAfterFinalAudio(sessionKey, "audio_stopped");
            return;
          }

          if (isPracticePausedRef.current) {
            prePauseListeningStateRef.current = "thinking";
            studentReadyToAnswerRef.current = false;
            setMicrophoneTrackEnabled(false);
            studentCanRespondRef.current = false;
            setWaitingForReadyPressValue(false);
            return;
          }

          if (waitingForReadyPressRef.current) {
            debugLog("[ORAL DEBUG] Duplicate audio_stopped ignored while waiting for Ready press");
            return;
          }

          const promptAction = pendingTutorPromptActionRef.current || "next_question";
          if (promptAction === "followup") {
            setListeningState("muted");
          }
          assistantAudioActiveRef.current = false;
          maybeClearRealtimeResponseActive("audio_stopped");
          releaseAssistantAudioGateAfterBuffer("audio_stopped");
        });

        sessionRef.current = realtimeSession;
        sessionMetaRef.current = {
          sessionId: sessionPayload.session_id || "",
          currentQuestionIndex: sessionPayload.current_question_index || 1,
          userEmail: sessionUserEmail,
          userId: sessionUserContext.user_id || sessionPayload.user_id || "",
          lastSpokenPrompt: openingPrompt,
          closed: false,
        };
        startupGuardRef.current = true;
        setListeningState("teacher_speaking");
        firstTutorUtteranceDoneRef.current = false;

        // 5) Connect
        setSessionStatus("Connecting voice agent...");
        await attempt.wait("connecting_realtime", () => realtimeSession.connect({ apiKey: clientSecret }),
          CONNECTION_TIMEOUT_MS, () => {
            try { realtimeSession.close(); } catch (_) {}
          });
        setIsConnected(true);

        appendLog("Tutor", openingPrompt);
        pendingTutorPromptActionRef.current = "next_question";
        setSessionStatus("Connected — oral exam started");

        // 6) Force the opening question
        sendTutorRealtimeMessage(
          realtimeSession,
          `Begin the oral exam now. Speak exactly this prompt and nothing else: ${openingPrompt}`,
          "startup_force_prompt",
          sessionKey
        );
        setSessionStatus("Tutor speaking first question...");
        waitForAssistantAudioEnd("awaiting_answer");
      }, (failure) => {
        stopSession({ reason: "voice startup failed", statusAfter: "Voice could not start. Try again below." });
        if (isPublicSessionError(failure)) {
          clearPublicAuthentication("Your session has expired. Please verify your email again.");
          return false;
        }
        if (isInsufficientOralAccessError(failure)) {
          handleInsufficientOralAccessError(failure);
          return false;
        }
      });
    } catch (err) {
      if (isPublicSessionError(err)) {
        clearPublicAuthentication("Your session has expired. Please verify your email again.");
        setSessionStatus("Your session has expired. Please verify your email again.");
        stopSession();
        return;
      }
      if (isInsufficientOralAccessError(err)) {
        handleInsufficientOralAccessError(err);
        return;
      }

      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to start oral exam session";
      setListeningState("muted");
      stopSession({ statusAfter: `Error: ${msg}` });
    } finally {
      startRequestPendingRef.current = false;
      setStartingSession(false);
    }
  };

  const listeningConfig =
    voiceStartup.error ? { label: "Voice could not start ? see recovery options", border: "#b42318", background: "#fef2f2", text: "#991b1b" } :
    voiceStartup.busy ? { label: voiceStartup.stage === "opening_microphone" ? "Opening microphone?" : "Connecting voice?", border: "#f97316", background: "#ffedd5", text: "#9a3412" } :
    LISTENING_STATE_CONFIG[listeningState] || LISTENING_STATE_CONFIG.muted;
  const livePanelStyle = {
    "--oral-status-border": listeningConfig.border,
    "--oral-status-bg": listeningConfig.background,
    "--oral-status-text": listeningConfig.text,
  };
  const statusCardStyle = {
    ...cardStyle,
    border: `1px solid ${listeningConfig.border}`,
    background: listeningConfig.background,
  };
  const paymentOptions = bootstrapErrorDetails?.payment_options || {};
  const accessNotice = getOralAccessNotice(bootstrapErrorDetails);
  const subscriptionPaymentUrl =
    paymentOptions?.subscription?.url || PUBLIC_ORAL_PRODUCTS_URL;
  const creditPaymentUrl =
    paymentOptions?.credits?.url || PUBLIC_ORAL_PRODUCTS_URL;
  const hasCreditPaymentOption = Boolean(paymentOptions?.credits);

  if (checkingPublicSession) return <div style={{ padding: 24 }}>Checking your account…</div>;
  if (publicSessionError) return <div style={{ padding: 24 }}><p>{publicSessionError}</p><button type="button" onClick={checkPublicSession}>Retry</button></div>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: 16, color: "#111827" }}>
      <h2 style={{ marginBottom: 8 }}>
        {isInterviewPractice ? "AI Interview Practice" : "Oral Exam Voice Practice"}
      </h2>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        {isInterviewPractice
          ? "Practise common, fundamental interview questions with an AI interviewer. Choose a question pack and complete the session to receive a feedback report."
          : "Verify your email, then choose a language, standard, topic set, and topic before starting the live oral practice session."}
      </p>
      <PublicAccountSummary user={publicUser} balances={publicBalances} onLogout={handlePublicLogout} loggingOut={loggingOut} />
      <div
        style={{
          ...cardStyle,
          marginBottom: 16,
          background: "#fffdf5",
          border: "1px solid #f59e0b",
          color: "#78350f",
        }}
      >
        The chatbot works best with headphones and a good microphone in a quiet environment. Make sure to allow microphone access when prompted.
        After you finish speaking, the {isInterviewPractice ? "interviewer" : "tutor"} response may take about 1 to 3
        seconds. It can take a little longer if your answer was longer, so please
        wait a moment before speaking again.
      </div>

      <div style={{ display: "grid", gap: 16 }}>
        {!emailVerified ? <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Verify Your Email</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                style={inputStyle}
                type="email"
                value={publicEmail}
                onChange={(e) => setPublicEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
              />
            </div>

            <div>
              <label style={labelStyle}>Email Verification Code</label>
              <input
                style={inputStyle}
                value={publicEmailCode}
                onChange={(e) => setPublicEmailCode(e.target.value)}
                placeholder="Enter email code"
                disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              marginTop: 12,
            }}
          >
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={handleSendEmailCode}
              disabled={
                sendingEmailCode ||
                emailSendCooldown > 0 ||
                voiceStartupLocked ||
                isConnected ||
                realtimeCleanupInProgress
              }
            >
              {emailSendCooldown > 0
                ? `Send Again (${emailSendCooldown}s)`
                : sendingEmailCode
                ? "Sending..."
                : "Send Email Code"}
            </button>

            <button
              type="button"
              style={primaryButtonStyle}
              onClick={handleVerifyEmailCode}
              disabled={
                !publicEmail.trim() ||
                !publicEmailCode.trim() ||
                verifyingEmailCode ||
                voiceStartupLocked ||
                isConnected ||
                realtimeCleanupInProgress
              }
            >
              {verifyingEmailCode ? "Verifying..." : "Verify Email"}
            </button>

            <div
              style={{
                color: emailVerified ? "#166534" : "#6b7280",
                fontSize: 14,
              }}
            >
              {emailVerificationStatus ||
                (emailCodeSent
                  ? "Enter the code from your email to continue."
                  : "Send an email code before verifying your class access.")}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <a href="/public-dashboard-embed" target="_blank" rel="noopener noreferrer">View My Account</a>
          </div>
        </div> : null}

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>
            {isInterviewPractice ? "Choose a Question Pack" : "Session Setup"}
          </h3>

          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                color: resolvedClassContext ? "#166534" : "#6b7280",
                fontSize: 14,
              }}
            >
              {verificationStatus ||
                (emailVerified
                  ? "Choose your language, topic set, standard, and topic."
                  : "Verify your email address first to unlock topic selection.")}
            </div>

            {bootstrapErrorDetails ? (
              <div
                style={{
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  borderRadius: 8,
                  color: "#9a3412",
                  padding: 12,
                  fontSize: 14,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 18 }}>
                  {accessNotice?.title ||
                    (isInterviewPractice
                      ? "Interview Practice Access Needed"
                      : "Oral Practice Access Needed")}
                </div>
                <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                  {accessNotice?.message ||
                    `Your trial usage for ${productNoun} has run out.`}
                </div>
                {accessNotice?.canChooseFreeTrial ? (
                  <button
                    type="button"
                    style={{ ...secondaryButtonStyle, marginTop: 12 }}
                    onClick={chooseFirstFreeTrialTopic}
                    disabled={!availableTopics.some(
                      (topic) =>
                        topic?.access_badge === "Free trial" &&
                        isTopicSelectable(topic)
                    )}
                  >
                    Choose a Free Trial Topic
                  </button>
                ) : null}
                <button
                  type="button"
                  style={{ ...primaryButtonStyle, marginTop: 12 }}
                  onClick={() =>
                    window.open(
                      subscriptionPaymentUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  View Plans and Credits
                </button>
                {hasCreditPaymentOption ? (
                  <button
                    type="button"
                    style={{ ...secondaryButtonStyle, marginTop: 12, marginLeft: 8 }}
                    onClick={() =>
                      window.open(
                        creditPaymentUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    Top Up Credits
                  </button>
                ) : null}
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  You can also top up credits from the products page.
                </div>
              </div>
            ) : null}

            {!bootstrapErrorDetails ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                }}
              >
                {!isInterviewPractice ? <div>
                  <label style={labelStyle}>Language</label>
                  <select
                    style={inputStyle}
                    value={selectedLanguageId || "english"}
                    onChange={(e) =>
                      setSelectedLanguageId(e.target.value || "english")
                    }
                    disabled={
                      !resolvedClassContext ||
                      loadingTopics ||
                      voiceStartupLocked ||
                      isConnected ||
                      realtimeCleanupInProgress
                    }
                  >
                    {(languages || []).map((language) => (
                      <option
                        key={getSafeLanguageId(language)}
                        value={getSafeLanguageId(language)}
                      >
                        {language.language_name ||
                          language.language_id ||
                          "English"}
                      </option>
                    ))}
                  </select>
                </div> : null}

                {!isInterviewPractice ? <div>
                  <label style={labelStyle}>Standard</label>
                  <select
                    style={inputStyle}
                    value={selectedStandard}
                    onChange={(e) => setSelectedStandard(e.target.value)}
                    disabled={
                      !resolvedClassContext ||
                      loadingTopics ||
                      voiceStartupLocked ||
                      isConnected ||
                      realtimeCleanupInProgress
                    }
                  >
                    {(standards || []).map((standard) => (
                      <option
                        key={standard.standard_id || standard.activity_set_id}
                        value={standard.standard_id || standard.activity_set_id}
                      >
                        {standard.standard_name || standard.activity_set_name}
                      </option>
                    ))}
                  </select>
                </div> : null}

                {!isInterviewPractice ? <div>
                  <label style={labelStyle}>Topic Set</label>
                  <select
                    style={inputStyle}
                    value={selectedTopicSetId}
                    onChange={(e) => setSelectedTopicSetId(e.target.value)}
                    disabled={
                      !resolvedClassContext ||
                      loadingTopics ||
                      voiceStartupLocked ||
                      isConnected ||
                      realtimeCleanupInProgress
                    }
                  >
                    {(topicSets || []).map((topicSet) => (
                      <option key={topicSet.topic_set_id} value={topicSet.topic_set_id}>
                        {topicSet.topic_set_name}
                      </option>
                    ))}
                  </select>
                </div> : null}

                <div>
                  <label style={labelStyle}>{isInterviewPractice ? "Question Pack" : "Topic"}</label>
                  {isInterviewPractice ? <select
                    style={inputStyle}
                    value={selectedTopicSetId}
                    onChange={(e) => setSelectedTopicSetId(e.target.value)}
                    disabled={
                      !resolvedClassContext ||
                      loadingTopics ||
                      voiceStartupLocked ||
                      isConnected ||
                      realtimeCleanupInProgress
                    }
                  >
                    {getPracticeSelectorItems(true, interviewPacks, availableTopics).map(
                      (item) => (
                        <option
                          key={item.topic_set_id}
                          value={item.topic_set_id}
                        >
                          {item.topic_set_name}
                        </option>
                      )
                    )}
                  </select> : (
                    <OralTopicSelect
                      topics={availableTopics}
                      value={selectedTopicId}
                      onChange={setSelectedTopicId}
                      style={inputStyle}
                      disabled={
                        !resolvedClassContext ||
                        loadingTopics ||
                        voiceStartupLocked ||
                        isConnected ||
                        realtimeCleanupInProgress
                      }
                    />
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Thinking Time</label>
                  <select
                    style={inputStyle}
                    value={selectedPauseDurationMode}
                    onChange={(e) => setSelectedPauseDurationMode(e.target.value)}
                    disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
                  >
                    {PAUSE_DURATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13, lineHeight: 1.45 }}>
                    How long the AI waits after you stop speaking before responding or moving to the next question. Choose Extended if you'd like more time to think before the AI continues.
                  </div>
                </div>
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={refreshSelectedTopicDisplay}
                disabled={
                  !resolvedClassContext ||
                  loadingTopics ||
                  !!bootstrapErrorDetails ||
                  (!isInterviewPractice && (!selectedStandard || !selectedTopicId)) ||
                  (isInterviewPractice && !selectedTopicSetId) ||
                  realtimeCleanupInProgress
                }
              >
                {isInterviewPractice ? "Refresh Question Packs" : "Refresh Topic Questions"}
              </button>
            </div>

            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
              }}
            >
              <OralVoiceRecovery error={voiceStartup.error} onRetry={voiceStartup.retry} />
              <div><b>Status:</b> {sessionStatus}</div>
              {!isInterviewPractice ? <div><b>Session ID:</b> {sessionInfo.sessionId || "-"}</div> : null}
              <div><b>Email Address:</b> {studentName.trim() || publicEmail.trim() || "-"}</div>
              {!isInterviewPractice ? <div>
                <b>Class:</b> {resolvedClassContext?.student_class || "-"}
              </div> : null}
              {!isInterviewPractice ? <div><b>Year:</b> {resolvedClassContext?.year || "-"}</div> : null}
              {!isInterviewPractice ? <div>
                <b>Language:</b>{" "}
                {selectedLanguage?.language_name || sessionInfo.languageId || "English"}
              </div> : null}
              <div>
                <b>{isInterviewPractice ? "Question Pack" : "Topic Set"}:</b>{" "}
                {isInterviewPractice
                  ? selectedInterviewPack?.topic_set_name || sessionInfo.topicTitle || "-"
                  : selectedTopicSet?.topic_set_name || sessionInfo.topicSetId || "Default Topics"}
              </div>
              <div>
                <b>Current Question:</b>{" "}
                {sessionInfo.questionCount
                  ? `${sessionInfo.currentQuestionIndex || "-"} of ${sessionInfo.questionCount}`
                  : sessionInfo.currentQuestionIndex || "-"}
              </div>
              <div><b>Report:</b> {reportStatus || (reportUrl ? "Ready" : "-")}</div>
              {ORAL_DEBUG ? (
                <>
                  <div><b>Realtime:</b> {isConnected ? "Connected" : "Not connected"}</div>
                  <div>
                    <b>studentReadyToAnswer:</b>{" "}
                    {studentReadyToAnswerRef.current ? "true" : "false"}
                  </div>
                  <div><b>readyButtonPressedAt:</b> {readyButtonPressedAt || "-"}</div>
                  <div>
                    <b>ignoredTranscriptBeforeReady:</b> {ignoredTranscriptBeforeReady}
                  </div>
                </>
              ) : null}

              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  borderRadius: 8,
                  background: listeningConfig.background,
                  color: listeningConfig.text,
                  border: `1px solid ${listeningConfig.border}`,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                {listeningConfig.label}
              </div>
              <div
                style={{
                  marginTop: 6,
                  color: listeningConfig.text,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                {studentCanRespond
                  ? "Speak clearly now."
                  : "Take your time. Tap Ready to Answer when prepared."}
              </div>
            </div>

            {reportUrl && (
              <div>
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={() =>
                    window.open(reportUrl, "_blank", "noopener,noreferrer")
                  }
                >
                  {isInterviewPractice ? "Open Interview Report" : "Open Graded Report"}
                </button>
              </div>
            )}

            {loadingTopics && (
              <div>{isInterviewPractice ? "Loading question packs..." : "Loading topics..."}</div>
            )}
            {isInterviewPractice && emailVerified && !loadingTopics && !topicsError && interviewPacks.length === 0 ? (
              <div style={{ color: "#b42318" }}>
                No interview question packs are available right now. Please try again later.
              </div>
            ) : null}
            {!!topicsError && (
            <div style={{ color: "#b42318" }}>
              {isInterviewPractice ? "Failed to load question packs" : "Failed to load topics"}: {topicsError}
            </div>
            )}
          </div>
        </div>

        <div className="oral-live-panel" style={livePanelStyle}>
          <div className="oral-status-card" style={statusCardStyle}>
            <div className="oral-status-banner">{listeningConfig.label}</div>
          </div>

        <div className="oral-stimulus-panel" style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Stimulus and Questions</h3>

          {stimulus.mediaType === "image" && stimulus.displayUrl ? (
            <div
              style={{
                minHeight: 520,
                maxHeight: 760,
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#f3f4f6",
                overflow: "hidden",
                padding: 12,
              }}
            >
              <img
                src={stimulus.displayUrl}
                alt={selectedTopic?.topic_title || "Stimulus"}
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: 736,
                  objectFit: "contain",
                  borderRadius: 10,
                  background: "#f3f4f6",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          ) : stimulus.mediaType === "video" && stimulus.displayUrl ? (
            <div
              style={{
                minHeight: 420,
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#111827",
                overflow: "hidden",
                padding: 12,
              }}
            >
              <iframe
                src={stimulus.displayUrl}
                title={selectedTopic?.topic_title || "Video stimulus"}
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  border: "none",
                  borderRadius: 10,
                  background: "#000",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : !isInterviewPractice ? (
            <div
              style={{
                minHeight: 420,
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                border: "1px dashed #d1d5db",
                background: "#f9fafb",
                color: "#6b7280",
              }}
            >
              No stimulus media available
            </div>
          ) : null}

          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 22 }}>
              {isInterviewPractice
                ? selectedInterviewPack?.topic_set_name || sessionInfo.topicTitle || "-"
                : selectedTopic?.topic_title || sessionInfo.topicTitle || "-"}
            </div>
            <div style={{ marginTop: 8, color: "#4b5563", lineHeight: 1.6 }}>
              {stimulus.description ||
                (isInterviewPractice
                  ? selectedInterviewPack?.short_description ||
                    selectedInterviewPack?.description ||
                    selectedInterviewPack?.category
                  : selectedTopic?.short_description || selectedTopic?.description) ||
                selectedTopic?.picture_stimulus ||
                sessionInfo.pictureStimulus ||
                (isInterviewPractice
                  ? "A curated pack of fundamental interview questions."
                  : "The selected topic description will appear here after session start.")}
            </div>
            {isInterviewPractice ? (
              <div style={{ marginTop: 10, color: "#4b5563", fontSize: 14 }}>
                {selectedInterviewPack?.expected_question_count ?? selectedInterviewPack?.question_count ? (
                  <span style={{ marginRight: 16 }}>
                    <b>Expected questions:</b>{" "}
                    {selectedInterviewPack?.expected_question_count ?? selectedInterviewPack?.question_count}
                  </span>
                ) : null}
                {selectedInterviewPack?.estimated_duration ?? selectedInterviewPack?.estimated_duration_minutes ? (
                  <span>
                    <b>Estimated duration:</b>{" "}
                    {selectedInterviewPack?.estimated_duration ?? `${selectedInterviewPack?.estimated_duration_minutes} minutes`}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {!isInterviewPractice ? <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>
              Questions
            </div>
            <ol style={{ margin: 0, paddingLeft: 24, color: "#374151", lineHeight: 1.8 }}>
              {orderedDisplayQuestions.length > 0 ? (
                orderedDisplayQuestions.map((question) => (
                  <li key={question.key}>{question.text}</li>
                ))
              ) : (
                <li>-</li>
              )}
            </ol>
          </div> : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
            <button
              type="button"
              style={primaryButtonStyle}
              onClick={startVoiceSession}
              disabled={
                !emailVerified ||
                loadingTopics ||
                voiceStartupLocked ||
                isConnected ||
                realtimeCleanupInProgress ||
                !!bootstrapErrorDetails ||
                !resolvedClassContext ||
                !userId ||
                (!isInterviewPractice && (!selectedStandard || !selectedTopicId)) ||
                (!isInterviewPractice && !isTopicSelectable(selectedTopic)) ||
                (isInterviewPractice && !selectedTopicSetId)
              }
            >
              {(startingSession || voiceStartup.busy)
                ? "Starting..."
                : isInterviewPractice
                ? "Start Interview Practice"
                : "Start Voice Practice"}
            </button>

            {isConnected &&
            waitingForReadyPress &&
            sessionInfo.status !== "completed" &&
            !studentCanRespond ? (
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={handleReadyToAnswer}
              >
                I'm Ready to Answer
              </button>
            ) : null}

            <button
              type="button"
              style={dangerButtonStyle}
              onClick={stopSession}
              disabled={!isConnected || realtimeCleanupInProgress}
            >
              End Session
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={refreshSelectedTopicDisplay}
              disabled={
                !resolvedClassContext ||
                loadingTopics ||
                !!bootstrapErrorDetails ||
                (!isInterviewPractice && (!selectedStandard || !selectedTopicId)) ||
                (isInterviewPractice && !selectedTopicSetId) ||
                realtimeCleanupInProgress
              }
            >
              {isInterviewPractice ? "Refresh Question Packs" : "Refresh Topic Questions"}
            </button>
          </div>
        </div>
      <div className="oral-log-card" style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Session Log</h3>

        {eventLog.length === 0 ? (
          <div style={{ color: "#6b7280" }}>
            No session events yet.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {eventLog.map((item) => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 12,
                  background:
                    item.speaker === "Tutor"
                      ? "#eef2ff"
                      : item.speaker === "Student"
                      ? "#f9fafb"
                      : "#fff7ed",
                }}
              >
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                  <b>{item.speaker}</b> · {item.at}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {item.text.startsWith("http://") ||
                  item.text.startsWith("https://") ? (
                    <a
                      href={item.text}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.text}
                    </a>
                  ) : (
                    item.text
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

export default PublicOralExamEmbedPage;
